// GAVION deal visibility (spec docs/superpowers/specs/2026-10-20-deal-visibility-roles-design.md §1–§2): the four roles
// of a deal and who may see it. Pure (no remote import): shared by the deal form, the demo's FakeRest twin and the AI
// context (contextRepo.ts). SQL authority: private.holds_deal_role and public.can_see_deal
// (supabase/schemas/65_gavion_deal_visibility.sql); scripts/gavion/opsParity.test.ts pins the column list.
export const DEAL_ROLES = [
  { role: "closer", column: "sales_id" },
  { role: "setter", column: "setter_sales_id" },
  { role: "account_manager", column: "account_manager_sales_id" },
  { role: "referrer", column: "referrer_sales_id" },
] as const;

export type DealRole = (typeof DEAL_ROLES)[number]["role"];
export type DealRoleColumn = (typeof DEAL_ROLES)[number]["column"];
export type DealRoleFields = { [K in DealRoleColumn]?: unknown };
export type DealViewer = { salesId: unknown; isAdmin: boolean };

const key = (v: unknown): string | null =>
  v === null || v === undefined || v === "" ? null : String(v);

/** He holds one of the four roles on a deal that has a Closer (a deal with no Closer is the admins' only). */
export const holdsDealRole = (
  deal: DealRoleFields,
  salesId: unknown,
): boolean => {
  const me = key(salesId);
  if (me === null || key(deal.sales_id) === null) return false;
  return DEAL_ROLES.some(({ column }) => key(deal[column]) === me);
};

/** public.can_see_deal: an admin sees every deal; anyone else, the deals he holds a role on. */
export const canSeeDeal = (deal: DealRoleFields, viewer: DealViewer): boolean =>
  viewer.isAdmin || holdsDealRole(deal, viewer.salesId);

// GAVION deal amount visibility (owner rule 2026-10-20; SQL authority: private.holds_amount_role /
// public.can_see_deal_amount / private.amount_hidden_for, supabase/schemas/72_gavion_deal_amount_visibility.sql):
// « Les setters ne peuvent pas voir la valeur du deal. Seulement les closers et responsables du compte. »
/** The roles that see the deal's amount (opsParity pins them against the SQL). */
export const AMOUNT_ROLES = [
  { role: "closer", column: "sales_id" },
  { role: "account_manager", column: "account_manager_sales_id" },
] as const;

/** He is the Closer or the Responsable de compte of a deal that has a Closer (Setter + Closer: the strongest role wins). */
export const holdsAmountRole = (
  deal: DealRoleFields,
  salesId: unknown,
): boolean => {
  const me = key(salesId);
  if (me === null || key(deal.sales_id) === null) return false;
  return AMOUNT_ROLES.some(({ column }) => key(deal[column]) === me);
};

/** public.can_see_deal_amount: an admin sees every amount; anyone else, those of the deals he is Closer or Responsable de compte of. */
export const canSeeDealAmount = (
  deal: DealRoleFields,
  viewer: DealViewer,
): boolean => viewer.isAdmin || holdsAmountRole(deal, viewer.salesId);

/** private.amount_hidden_for: he holds a role on the deal, and only Setter and/or Apporteur (the reports' mask). */
export const amountHiddenFor = (
  deal: DealRoleFields,
  viewer: DealViewer | null,
): boolean =>
  viewer !== null &&
  !viewer.isAdmin &&
  holdsDealRole(deal, viewer.salesId) &&
  !holdsAmountRole(deal, viewer.salesId);
